import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  Building2, Users, Settings, ShoppingBag, MessageSquare, Target,
  ArrowRight, ArrowLeft, Loader2, CheckCircle2, Sparkles, ChevronRight,
  MapPin, Truck, Monitor, Wrench, CreditCard, Globe
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = [
  { id: 'identity', label: 'Business Identity', icon: Building2, fields: 5 },
  { id: 'team', label: 'Team & Workforce', icon: Users, fields: 6 },
  { id: 'operations', label: 'Operations', icon: Settings, fields: 6 },
  { id: 'sales', label: 'Sales & Finance', icon: ShoppingBag, fields: 7 },
  { id: 'communication', label: 'Communication', icon: MessageSquare, fields: 3 },
  { id: 'goals', label: 'Goals & Priorities', icon: Target, fields: 3 },
];

const INDUSTRIES = [
  'Retail', 'Food & Beverage', 'Professional Services', 'Construction & Engineering',
  'Logistics & Delivery', 'Healthcare & Wellness', 'IT & Technology', 'Education & Training',
  'Hospitality & Tourism', 'Manufacturing', 'Real Estate', 'Event Management',
  'Vending & Amenity Services', 'Coffee & Beverage Distribution', 'Other',
];

const LEGAL_STRUCTURES = [
  'Sole Proprietorship', 'Partnership', 'LLC/Ltd', 'Corporation', 'Cooperative', 'NGO/Non-profit', 'Freelancer/Independent',
];

const TEAM_SIZES = [
  { value: '1', label: 'Just me' }, { value: '2-5', label: '2-5' },
  { value: '6-20', label: '6-20' }, { value: '21-50', label: '21-50' },
  { value: '51-200', label: '51-200' }, { value: '200+', label: '200+' },
];

const WORK_MODELS = [
  { value: 'office', label: 'Office-based' }, { value: 'field', label: 'Field/Outdoor' },
  { value: 'remote', label: 'Remote/WFH' }, { value: 'mobile', label: 'Mobile' },
  { value: 'multi-location', label: 'Multiple Locations' },
];

const BILLING_MODELS = [
  { value: 'one-time', label: 'One-time' }, { value: 'recurring', label: 'Recurring/Subscription' },
  { value: 'project', label: 'Project/Milestone' }, { value: 'retainer', label: 'Retainer' },
  { value: 'usage', label: 'Per-usage' },
];

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' }, { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'card', label: 'Credit/Debit Card' }, { value: 'digital_wallet', label: 'Digital Wallets' },
];

const COMM_CHANNELS = [
  { value: 'email', label: 'Email' }, { value: 'sms', label: 'SMS/WhatsApp' },
  { value: 'phone', label: 'Phone' }, { value: 'in-person', label: 'In-person' },
  { value: 'portal', label: 'Client Portal' },
];

const NOTIF_PREFS = [
  { value: 'email', label: 'Email' }, { value: 'sms', label: 'SMS' },
  { value: 'in-app', label: 'In-app' }, { value: 'push', label: 'Push' },
];

const GOALS = [
  'Invoicing & payments', 'Attendance tracking', 'Field operations',
  'Client relationships', 'Proposals & quotations', 'Inventory & stock',
  'HR & payroll', 'Sales & leads', 'All-in-one management',
];

export default function SmartSetupPage() {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const [bpo, setBpo] = useState({
    legal_structure: '', industry: '', other_industry: '', business_nature: '', customer_type: '',
    country: '', currency: 'BHD', team_size_band: '1', work_model: [],
    has_hr_dept: false, attendance_tracking: false, field_staff: false,
    payroll_preference: '', physical_location: '', has_warehouse: false,
    has_delivery: false, has_pos: false, has_field_service: false, has_assets: false,
    invoicing_readiness: '', billing_model: [], sends_proposals: false,
    client_import: 'fresh', has_crm_pipeline: false, has_sales_team: false,
    payment_methods: [], client_comm_channels: [], notification_preferences: ['email', 'in-app'],
    needs_client_portal: false, primary_goals: [], launch_urgency: '',
    platform_experience: '',
  });

  // Auto-detect country
  useEffect(() => {
    fetch('https://ipapi.co/json/').then(r => r.json()).then(d => {
      if (d.country_name) setBpo(p => ({ ...p, country: d.country_name, currency: d.currency || 'BHD' }));
    }).catch(() => {});
  }, []);

  const update = (key, val) => setBpo(p => ({ ...p, [key]: val }));
  const toggleArray = (key, val) => setBpo(p => ({
    ...p, [key]: p[key].includes(val) ? p[key].filter(v => v !== val) : [...p[key], val]
  }));

  const progress = ((step + 1) / SECTIONS.length) * 100;

  const handleComplete = async () => {
    setSaving(true);
    try {
      const payload = { ...bpo };
      if (payload.industry === 'Other' && payload.other_industry) payload.industry = payload.other_industry;
      delete payload.other_industry;
      await axios.post(`${API}/api/spq/save`, { business_profile: payload });
      await refreshUser();
      toast.success('Smart Setup complete!');
      navigate('/dashboard');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleSkip = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/api/spq/skip`);
      await refreshUser();
      navigate('/dashboard');
    } catch { toast.error('Failed to skip'); }
    finally { setSaving(false); }
  };

  const sec = SECTIONS[step];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-emerald-50/30" data-testid="smart-setup-page">
      <div className="max-w-2xl mx-auto px-4 py-6 sm:py-10">
        {/* Header */}
        <div className="text-center mb-6">
          <img src="/konnect-logo.png" alt="Konnect" className="h-9 w-auto object-contain mx-auto mb-3" />
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Smart Business Setup</h1>
          <p className="text-sm text-gray-500 mt-1">Tell us about your business — we'll personalize everything for you</p>
        </div>

        {/* Progress */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            {SECTIONS.map((s, i) => (
              <button key={s.id} onClick={() => i <= step && setStep(i)}
                className={cn("flex items-center gap-1 text-[10px] font-medium transition-colors",
                  i === step ? "text-emerald-600" : i < step ? "text-emerald-400 cursor-pointer" : "text-gray-300"
                )}>
                <s.icon className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">{s.label}</span>
              </button>
            ))}
          </div>
          <Progress value={progress} className="h-1.5" />
          <p className="text-[10px] text-gray-400 mt-1 text-right">Step {step + 1} of {SECTIONS.length}</p>
        </div>

        {/* Card */}
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="p-5 sm:p-7">
            <div className="flex items-center gap-2 mb-5">
              <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                <sec.icon className="h-4 w-4 text-emerald-600" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">{sec.label}</h2>
            </div>

            <div className="space-y-4">
              {step === 0 && <SectionIdentity bpo={bpo} update={update} />}
              {step === 1 && <SectionTeam bpo={bpo} update={update} toggleArray={toggleArray} />}
              {step === 2 && <SectionOperations bpo={bpo} update={update} />}
              {step === 3 && <SectionSales bpo={bpo} update={update} toggleArray={toggleArray} />}
              {step === 4 && <SectionComms bpo={bpo} toggleArray={toggleArray} update={update} />}
              {step === 5 && <SectionGoals bpo={bpo} update={update} toggleArray={toggleArray} />}
            </div>
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-5">
          <Button variant="ghost" size="sm" onClick={() => step > 0 ? setStep(step - 1) : null}
            disabled={step === 0} className="text-gray-500">
            <ArrowLeft className="h-4 w-4 mr-1" />Back
          </Button>
          <Button variant="ghost" size="sm" onClick={handleSkip} className="text-gray-400 text-xs" disabled={saving}>
            Skip for now
          </Button>
          {step < SECTIONS.length - 1 ? (
            <Button size="sm" onClick={() => setStep(step + 1)} className="bg-emerald-600 hover:bg-emerald-700">
              Next<ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button size="sm" onClick={handleComplete} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
              Complete Setup
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}


// ─── Section Components ──────────────────────────────────────────────────────

function Q({ label, sub, children }) {
  return (
    <div>
      <Label className="text-sm font-semibold text-gray-800">{label}</Label>
      {sub && <p className="text-[11px] text-gray-400 mb-2">{sub}</p>}
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function Chips({ options, value, onChange, multi = false }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map(opt => {
        const v = typeof opt === 'string' ? opt : opt.value;
        const l = typeof opt === 'string' ? opt : opt.label;
        const active = multi ? (value || []).includes(v) : value === v;
        return (
          <button key={v} onClick={() => multi ? onChange(v) : onChange(v)}
            className={cn("px-3 py-1.5 text-xs rounded-full border transition-all",
              active ? "bg-emerald-100 border-emerald-400 text-emerald-700 font-medium" : "border-gray-200 text-gray-500 hover:border-emerald-300"
            )}>{l}</button>
        );
      })}
    </div>
  );
}

function Toggle({ label, value, onChange }) {
  return (
    <div className="flex items-center justify-between py-2 px-3 rounded-lg border border-gray-100 hover:border-gray-200 transition-colors">
      <span className="text-sm text-gray-700">{label}</span>
      <Switch checked={value} onCheckedChange={onChange} />
    </div>
  );
}


function SectionIdentity({ bpo, update }) {
  return (
    <>
      <Q label="Legal Structure">
        <Chips options={LEGAL_STRUCTURES} value={bpo.legal_structure} onChange={v => update('legal_structure', v)} />
      </Q>
      <Q label="Industry">
        <Select value={bpo.industry} onValueChange={v => update('industry', v)}>
          <SelectTrigger data-testid="industry-select"><SelectValue placeholder="Select your industry" /></SelectTrigger>
          <SelectContent>{INDUSTRIES.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
        </Select>
        {bpo.industry === 'Other' && <Input value={bpo.other_industry} onChange={e => update('other_industry', e.target.value)} placeholder="Specify your industry" className="mt-2" />}
      </Q>
      <Q label="What does your business do?">
        <Chips options={[
          { value: 'products', label: 'Sell physical products' },
          { value: 'services', label: 'Provide services' },
          { value: 'both', label: 'Products + Services' },
          { value: 'marketplace', label: 'Marketplace/Aggregator' },
        ]} value={bpo.business_nature} onChange={v => update('business_nature', v)} />
      </Q>
      <Q label="Primary customers">
        <Chips options={[
          { value: 'B2B', label: 'Businesses (B2B)' },
          { value: 'B2C', label: 'Consumers (B2C)' },
          { value: 'both', label: 'Both B2B + B2C' },
          { value: 'government', label: 'Government/Institutions' },
        ]} value={bpo.customer_type} onChange={v => update('customer_type', v)} />
      </Q>
      <Q label="Country & Currency">
        <div className="grid grid-cols-2 gap-2">
          <Input value={bpo.country} onChange={e => update('country', e.target.value)} placeholder="Country" />
          <Input value={bpo.currency} onChange={e => update('currency', e.target.value)} placeholder="Currency (e.g. BHD)" />
        </div>
      </Q>
    </>
  );
}


function SectionTeam({ bpo, update, toggleArray }) {
  return (
    <>
      <Q label="Team size">
        <Chips options={TEAM_SIZES} value={bpo.team_size_band} onChange={v => update('team_size_band', v)} />
      </Q>
      <Q label="Work model" sub="Select all that apply">
        <Chips options={WORK_MODELS} value={bpo.work_model} onChange={v => toggleArray('work_model', v)} multi />
      </Q>
      <Toggle label="Dedicated HR department or HR manager?" value={bpo.has_hr_dept} onChange={v => update('has_hr_dept', v)} />
      <Toggle label="Need to track attendance or shifts?" value={bpo.attendance_tracking} onChange={v => update('attendance_tracking', v)} />
      <Toggle label="Staff who visit client sites or work outdoors?" value={bpo.field_staff} onChange={v => update('field_staff', v)} />
      <Q label="Payroll preference">
        <Chips options={[
          { value: 'internal', label: 'Handle internally' },
          { value: 'external', label: 'External software' },
          { value: 'platform', label: 'Want payroll here' },
          { value: 'not applicable', label: 'Not applicable' },
        ]} value={bpo.payroll_preference} onChange={v => update('payroll_preference', v)} />
      </Q>
    </>
  );
}


function SectionOperations({ bpo, update }) {
  return (
    <>
      <Q label="Physical location?">
        <Chips options={[
          { value: 'single', label: 'Single location' },
          { value: 'multiple', label: 'Multiple locations' },
          { value: 'none', label: 'Fully online' },
          { value: 'home', label: 'Work from home' },
        ]} value={bpo.physical_location} onChange={v => update('physical_location', v)} />
      </Q>
      <Toggle label="Manage warehouse or inventory storage?" value={bpo.has_warehouse} onChange={v => update('has_warehouse', v)} />
      <Toggle label="Manage delivery or dispatch?" value={bpo.has_delivery} onChange={v => update('has_delivery', v)} />
      <Toggle label="Point-of-Sale at counter or kiosk?" value={bpo.has_pos} onChange={v => update('has_pos', v)} />
      <Toggle label="Manage job orders, work orders, or field service tickets?" value={bpo.has_field_service} onChange={v => update('has_field_service', v)} />
      <Toggle label="Manage equipment/assets needing maintenance?" value={bpo.has_assets} onChange={v => update('has_assets', v)} />
    </>
  );
}


function SectionSales({ bpo, update, toggleArray }) {
  return (
    <>
      <Q label="Current invoicing method">
        <Chips options={[
          { value: 'manual', label: 'Manually' },
          { value: 'external', label: 'External software' },
          { value: 'not billing', label: 'Not billing yet' },
          { value: 'want_here', label: 'Want to manage here' },
        ]} value={bpo.invoicing_readiness} onChange={v => update('invoicing_readiness', v)} />
      </Q>
      <Q label="Billing model" sub="Select all that apply">
        <Chips options={BILLING_MODELS} value={bpo.billing_model} onChange={v => toggleArray('billing_model', v)} multi />
      </Q>
      <Toggle label="Send proposals or quotations before invoicing?" value={bpo.sends_proposals} onChange={v => update('sends_proposals', v)} />
      <Q label="Existing client list">
        <Chips options={[
          { value: 'upload', label: 'Upload CSV/Excel' },
          { value: 'manual', label: 'Enter manually' },
          { value: 'fresh', label: 'Start fresh' },
        ]} value={bpo.client_import} onChange={v => update('client_import', v)} />
      </Q>
      <Toggle label="Track leads and sales pipeline?" value={bpo.has_crm_pipeline} onChange={v => update('has_crm_pipeline', v)} />
      <Toggle label="Sales team needing commission or target tracking?" value={bpo.has_sales_team} onChange={v => update('has_sales_team', v)} />
      <Q label="Payment methods accepted" sub="Select all that apply">
        <Chips options={PAYMENT_METHODS} value={bpo.payment_methods} onChange={v => toggleArray('payment_methods', v)} multi />
      </Q>
    </>
  );
}


function SectionComms({ bpo, toggleArray, update }) {
  return (
    <>
      <Q label="Client communication channels" sub="Select all that apply">
        <Chips options={COMM_CHANNELS} value={bpo.client_comm_channels} onChange={v => toggleArray('client_comm_channels', v)} multi />
      </Q>
      <Q label="Team notification preferences" sub="Select all that apply">
        <Chips options={NOTIF_PREFS} value={bpo.notification_preferences} onChange={v => toggleArray('notification_preferences', v)} multi />
      </Q>
      <Toggle label="Need a client-facing portal?" value={bpo.needs_client_portal} onChange={v => update('needs_client_portal', v)} />
    </>
  );
}


function SectionGoals({ bpo, update, toggleArray }) {
  return (
    <>
      <Q label="Primary reason for using the platform" sub="Select up to 3">
        <Chips options={GOALS} value={bpo.primary_goals}
          onChange={v => {
            if (bpo.primary_goals.includes(v)) toggleArray('primary_goals', v);
            else if (bpo.primary_goals.length < 3) toggleArray('primary_goals', v);
          }} multi />
      </Q>
      <Q label="Launch timeline">
        <Chips options={[
          { value: 'immediate', label: 'Immediately' },
          { value: '1month', label: 'Within a month' },
          { value: '3months', label: 'Within 3 months' },
          { value: 'exploring', label: 'Just exploring' },
        ]} value={bpo.launch_urgency} onChange={v => update('launch_urgency', v)} />
      </Q>
      <Q label="Prior platform experience">
        <Chips options={[
          { value: 'first_time', label: 'First time using a platform' },
          { value: 'migrating', label: 'Migrating from another tool' },
          { value: 'returning', label: 'Used one before' },
          { value: 'multi_tool', label: 'Currently using multiple tools' },
        ]} value={bpo.platform_experience} onChange={v => update('platform_experience', v)} />
      </Q>
    </>
  );
}
